/**
 * Centralized Logging Utility
 * Provides structured logging with different levels and output adapters
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
  NONE = 4
}

export interface LogEntry {
  level: LogLevel;
  timestamp: Date;
  message: string;
  context?: string;
  data?: unknown;
  tags?: string[];
}

export interface LogAdapter {
  log(entry: LogEntry): void;
}

/**
 * Console adapter for development
 */
class ConsoleAdapter implements LogAdapter {
  log(entry: LogEntry): void {
    const { level, timestamp, message, context, data } = entry;
    const prefix = `[${timestamp.toISOString()}] [${LogLevel[level]}]${context ? ` [${context}]` : ''}`;
    
    switch (level) {
      case LogLevel.DEBUG:
        console.debug(prefix, message, data);
        break;
      case LogLevel.INFO:
        console.info(prefix, message, data);
        break;
      case LogLevel.WARN:
        console.warn(prefix, message, data);
        break;
      case LogLevel.ERROR:
        console.error(prefix, message, data);
        break;
    }
  }
}

/**
 * Local storage adapter for persistence
 */
class LocalStorageAdapter implements LogAdapter {
  private readonly maxEntries = 1000;
  private readonly storageKey = 'app_logs';

  log(entry: LogEntry): void {
    try {
      const existingLogs = this.getLogs();
      existingLogs.push(this.serializeEntry(entry));
      
      // Keep only the most recent entries
      if (existingLogs.length > this.maxEntries) {
        existingLogs.splice(0, existingLogs.length - this.maxEntries);
      }
      
      localStorage.setItem(this.storageKey, JSON.stringify(existingLogs));
    } catch (error) {
      // Fail silently if localStorage is full or unavailable
      console.warn('Failed to persist log entry:', error);
    }
  }

  getLogs(): unknown[] {
    try {
      const logs = localStorage.getItem(this.storageKey);
      return logs ? JSON.parse(logs) : [];
    } catch {
      return [];
    }
  }

  clearLogs(): void {
    localStorage.removeItem(this.storageKey);
  }

  private serializeEntry(entry: LogEntry): Record<string, unknown> {
    return {
      ...entry,
      level: LogLevel[entry.level],
      timestamp: entry.timestamp.toISOString()
    };
  }
}

/**
 * Remote adapter for production (placeholder)
 */
class RemoteAdapter implements LogAdapter {
  private queue: LogEntry[] = [];
  private flushInterval: number = 5000; // 5 seconds
  private timer?: NodeJS.Timeout;

  constructor() {
    this.startFlushTimer();
  }

  log(entry: LogEntry): void {
    this.queue.push(entry);
    
    // Flush immediately for errors
    if (entry.level === LogLevel.ERROR) {
      this.flush();
    }
  }

  private startFlushTimer(): void {
    this.timer = setInterval(() => {
      this.flush();
    }, this.flushInterval);
  }

  private async flush(): Promise<void> {
    if (this.queue.length === 0) return;
    
    const entries = [...this.queue];
    this.queue = [];
    
    try {
      // In production, this would send to a logging service
      // await fetch('/api/logs', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({ entries })
      // });
      
      // For now, just log that we would send these
      if (process.env.NODE_ENV === 'production') {
        console.log('Would send logs to remote service:', entries);
      }
    } catch (error) {
      // Re-queue entries if send fails
      this.queue.unshift(...entries);
      console.error('Failed to send logs to remote service:', error);
    }
  }

  destroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.flush(); // Final flush
    }
  }
}

export class Logger {
  private static instance: Logger;
  private adapters: LogAdapter[] = [];
  private minLevel: LogLevel = LogLevel.DEBUG;
  private defaultContext?: string;

  private constructor() {
    this.setupAdapters();
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private setupAdapters(): void {
    // Always use console in development
    if (process.env.NODE_ENV === 'development') {
      this.adapters.push(new ConsoleAdapter());
      this.adapters.push(new LocalStorageAdapter());
    } else {
      // In production, use remote logging
      this.adapters.push(new RemoteAdapter());
      // Still use console for errors
      this.adapters.push(new ConsoleAdapter());
    }
  }

  setMinLevel(level: LogLevel): void {
    this.minLevel = level;
  }

  setDefaultContext(context: string): void {
    this.defaultContext = context;
  }

  addAdapter(adapter: LogAdapter): void {
    this.adapters.push(adapter);
  }

  private log(level: LogLevel, message: string, data?: unknown, context?: string, tags?: string[]): void {
    if (level < this.minLevel) return;

    const entry: LogEntry = {
      level,
      timestamp: new Date(),
      message,
      context: context || this.defaultContext,
      data,
      tags
    };

    this.adapters.forEach(adapter => {
      try {
        adapter.log(entry);
      } catch (error) {
        console.error('Logger adapter error:', error);
      }
    });
  }

  debug(message: string, data?: unknown, context?: string, tags?: string[]): void {
    this.log(LogLevel.DEBUG, message, data, context, tags);
  }

  info(message: string, data?: unknown, context?: string, tags?: string[]): void {
    this.log(LogLevel.INFO, message, data, context, tags);
  }

  warn(message: string, data?: unknown, context?: string, tags?: string[]): void {
    this.log(LogLevel.WARN, message, data, context, tags);
  }

  error(message: string, data?: unknown, context?: string, tags?: string[]): void {
    this.log(LogLevel.ERROR, message, data, context, tags);
  }

  /**
   * Create a child logger with a specific context
   */
  child(context: string): ContextualLogger {
    return new ContextualLogger(this, context);
  }

  /**
   * Log method execution with timing
   */
  async logAsync<T>(
    operation: string,
    fn: () => Promise<T>,
    context?: string
  ): Promise<T> {
    const startTime = Date.now();
    
    this.debug(`Starting ${operation}`, { startTime }, context);
    
    try {
      const result = await fn();
      const duration = Date.now() - startTime;
      
      this.info(`Completed ${operation}`, { duration, success: true }, context);
      
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      this.error(`Failed ${operation}`, { 
        duration, 
        success: false, 
        error: error instanceof Error ? error.message : error 
      }, context);
      
      throw error;
    }
  }

  /**
   * Log synchronous method execution with timing
   */
  logSync<T>(
    operation: string,
    fn: () => T,
    context?: string
  ): T {
    const startTime = Date.now();
    
    this.debug(`Starting ${operation}`, { startTime }, context);
    
    try {
      const result = fn();
      const duration = Date.now() - startTime;
      
      this.info(`Completed ${operation}`, { duration, success: true }, context);
      
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      
      this.error(`Failed ${operation}`, { 
        duration, 
        success: false, 
        error: error instanceof Error ? error.message : error 
      }, context);
      
      throw error;
    }
  }
}

/**
 * Contextual logger for specific components
 */
export class ContextualLogger {
  constructor(
    private parent: Logger,
    private context: string
  ) {}

  debug(message: string, data?: unknown, tags?: string[]): void {
    this.parent.debug(message, data, this.context, tags);
  }

  info(message: string, data?: unknown, tags?: string[]): void {
    this.parent.info(message, data, this.context, tags);
  }

  warn(message: string, data?: unknown, tags?: string[]): void {
    this.parent.warn(message, data, this.context, tags);
  }

  error(message: string, data?: unknown, tags?: string[]): void {
    this.parent.error(message, data, this.context, tags);
  }

  async logAsync<T>(operation: string, fn: () => Promise<T>): Promise<T> {
    return this.parent.logAsync(operation, fn, this.context);
  }

  logSync<T>(operation: string, fn: () => T): T {
    return this.parent.logSync(operation, fn, this.context);
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Performance monitoring decorator
export function LogPerformance(target: unknown, propertyName: string, descriptor: PropertyDescriptor) {
  const originalMethod = descriptor.value;
  
  descriptor.value = async function(...args: unknown[]) {
    const className = (target as { constructor: { name: string } }).constructor.name;
    const context = `${className}.${propertyName}`;
    
    return logger.logAsync(propertyName, () => originalMethod.apply(this, args), context);
  };
  
  return descriptor;
}

// Method logging decorator
export function LogMethod(level: LogLevel = LogLevel.DEBUG) {
  return function(target: unknown, propertyName: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    
    descriptor.value = function(...args: unknown[]) {
      const className = (target as { constructor: { name: string } }).constructor.name;
      const context = `${className}.${propertyName}`;
      
      logger.debug(`Calling ${propertyName}`, { args }, context);
      
      try {
        const result = originalMethod.apply(this, args);
        
        if (result instanceof Promise) {
          return result.then((value) => {
            logger.debug(`${propertyName} completed`, { result: value }, context);
            return value;
          }).catch((error) => {
            logger.error(`${propertyName} failed`, { error: error.message }, context);
            throw error;
          });
        }
        
        logger.debug(`${propertyName} completed`, { result }, context);
        return result;
      } catch (error) {
        logger.error(`${propertyName} failed`, { error: error instanceof Error ? error.message : error }, context);
        throw error;
      }
    };
    
    return descriptor;
  };
}